import { Link } from 'react-router-dom';
import TileStrip, { TileLegend } from './TileStrip.jsx';

// The portal's one KPI card (ENTRAID-347), the "Daily posture" look of the onGuard promo: a white card with a soft
// border, a small slate label, a huge navy number (Montserrat ExtraBold) with a smaller muted unit, one compact visual
// and a short muted caption. Documented in brand/onguard/README.md, "KPI cards".
//
// Variants (one visual per card, never more):
//   ring   a light-blue ring for a 0 to 100 score (percent), small, beside the number;
//   donut  the same ring, large, in the bottom section of the card with the caption beside it (a score card whose
//          number has no other visual: the card never leaves its lower half empty; product owner, 2026-10-22);
//   bar    a progress bar for a percentage (percent);
//   tiles  a strip of small square tiles for "n of total" counts (filled, total), the IPC tile language, or one
//          tile per day or item from `tileStates` ('on', 'off', 'warn', 'bad'; components/TileStrip.jsx). With
//          `tileVariant: 'status'` the states are per-source 'collected', 'pending', 'failed' (white squares with a
//          check, nothing or an X) and a legend with the counts follows the strip; `tileLabels` are hover tooltips;
//   summary compact rows under the number (`rows`: { key, count, label, tone, to?, linkLabel? }): a status dot, the
//          count in bold ink and the words, for example "30 completed", "0 failed", "7 pending (license or
//          permission)". A row with `to` is a link, so a card with linked rows is not itself a link (no nested links);
//   custom one compact visual the caller draws (`visual`, aria-hidden or text), for a breakdown no other variant
//          fits (Security "Overall security measures": pass and fail bars per severity);
//   none   the number and caption only.
// Every visual is decoration for the number beside it (aria-hidden): the value is always visible text. A card with
// `to` is a router Link (hover and focus ring); the caller passes the exact aria-label the link must announce.
// Data is the caller's: the card never invents a value, a delta or a caption.

const finite = (n) => typeof n === 'number' && Number.isFinite(n);
const clampPercent = (n) => (finite(n) ? Math.max(0, Math.min(100, n)) : 0);

/** Most tiles a strip draws; above it each tile stands for an equal share of the total. */
export const MAX_TILES = 48;

// Status dots read the portal tokens (src/styles.css): good, warn-dot, bad. Never a fill.
const TONE_DOT = Object.freeze({ good: 'bg-ipc-good', warn: 'bg-[var(--warn-dot)]', bad: 'bg-ipc-bad' });

/** A short number-like value ("86", "1,204", "58.1", "--") gets the huge size; words get a smaller one. */
export function isNumberLike(value) {
  if (typeof value === 'number') return true;
  const s = String(value ?? '');
  return s.length > 0 && s.length <= 8 && /^[\d.,+-]+$/.test(s);
}

/** The tiles a strip draws for `filled` of `total`: one per item, or MAX_TILES equal shares above it. */
export function tileCounts(filled, total, max = MAX_TILES) {
  const t = finite(total) && total > 0 ? Math.floor(total) : 0;
  const f = finite(filled) ? Math.max(0, Math.min(t, Math.floor(filled))) : 0;
  if (t <= max) return { on: f, all: t };
  return { on: Math.round((f / t) * max), all: max };
}

function Ring({ percent }) {
  const p = clampPercent(percent);
  return (
    <svg className="kpi-ring shrink-0" viewBox="0 0 100 100" aria-hidden="true" focusable="false" data-kpi-visual="ring">
      <circle cx="50" cy="50" r="41" fill="none" stroke="var(--kpi-track)" strokeWidth="12" />
      <circle
        cx="50" cy="50" r="41" fill="none" stroke="var(--sky)" strokeWidth="12"
        pathLength="100" strokeDasharray="100" strokeDashoffset={100 - p}
        transform="rotate(-90 50 50)"
      />
    </svg>
  );
}

function Donut({ percent, caption, tone }) {
  const p = clampPercent(percent);
  return (
    <div className="mt-auto flex items-center gap-4 pt-5" data-kpi-donut="">
      <svg className="kpi-donut shrink-0" viewBox="0 0 100 100" aria-hidden="true" focusable="false" data-kpi-visual="donut">
        <circle cx="50" cy="50" r="42" fill="none" stroke="var(--kpi-track)" strokeWidth="11" />
        <circle
          cx="50" cy="50" r="42" fill="none" stroke="var(--sky)" strokeWidth="11"
          pathLength="100" strokeDasharray="100" strokeDashoffset={100 - p}
          transform="rotate(-90 50 50)"
        />
      </svg>
      {caption ? (
        <div className="flex min-w-0 items-center gap-2 text-sm text-ipc-muted" data-kpi-caption="">
          {tone && TONE_DOT[tone] ? <span className={`block h-2.5 w-2.5 shrink-0 rounded-sm ${TONE_DOT[tone]}`} aria-hidden="true" /> : null}
          <span className="min-w-0">{caption}</span>
        </div>
      ) : null}
    </div>
  );
}

// Summary row dots: the status tokens, and the pale track for a zero count (nothing to flag).
const ROW_DOT = Object.freeze({ good: 'var(--good)', warn: 'var(--warn-dot)', bad: 'var(--bad)', none: 'var(--kpi-track)' });

function SummaryRows({ rows }) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (list.length === 0) return null;
  return (
    <ul className="mt-4 space-y-1.5 text-sm" data-kpi-visual="summary">
      {list.map((r) => {
        const dot = ROW_DOT[r.tone] ?? ROW_DOT.none;
        const text = (
          <>
            <span className="block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: dot }} aria-hidden="true" />
            <span className="min-w-0"><span className="font-semibold tabular-nums text-ipc-ink">{r.count}</span> <span className="text-ipc-ink">{r.label}</span></span>
          </>
        );
        return (
          <li key={r.key} data-kpi-row={r.key}>
            {r.to ? (
              <Link to={r.to} aria-label={r.linkLabel} className="inline-flex items-center gap-2 rounded underline decoration-ipc-field underline-offset-2 hover:decoration-ipc-light">{text}</Link>
            ) : (
              <span className="inline-flex items-center gap-2">{text}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Bar({ percent }) {
  return (
    <div className="mt-4 h-3 w-full" style={{ background: 'var(--kpi-track)' }} aria-hidden="true" data-kpi-visual="bar">
      <div className="h-full bg-ipc-light" style={{ width: `${clampPercent(percent)}%` }} />
    </div>
  );
}

/** The tile states for `filled` of `total` (the shared TileStrip draws them). */
export function tileStates(filled, total) {
  const { on, all } = tileCounts(filled, total);
  return Array.from({ length: all }, (_, i) => (i < on ? 'on' : 'off'));
}

function Tiles({ filled, total, states, variant, labels }) {
  if (variant === 'status') {
    // Every source is drawn (no MAX_TILES shares): one square per source, and the legend counts all of them.
    const list = Array.isArray(states) ? states : [];
    const counts = { collected: 0, pending: 0, failed: 0 };
    for (const s of list) if (s in counts) counts[s] += 1;
    return (
      <>
        <TileStrip states={list} variant="status" labels={labels} className="mt-4" data-kpi-visual="tiles" />
        {list.length > 0 ? <TileLegend counts={counts} className="mt-2" /> : null}
      </>
    );
  }
  const list = Array.isArray(states) && states.length > 0 ? states.slice(0, MAX_TILES) : tileStates(filled, total);
  return <TileStrip states={list} className="mt-4" data-kpi-visual="tiles" />;
}

const FRAME = 'kpi-card relative flex min-w-0 flex-col rounded-2xl border bg-white p-5 sm:p-6';
const INTERACTIVE = 'transition hover:border-ipc-light hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ipc-light';

/**
 * @param {{
 *   label: string, value?: string|number|null, unit?: string, caption?: import('react').ReactNode,
 *   tone?: 'good'|'warn'|'bad', variant?: 'ring'|'bar'|'tiles'|'none', percent?: number|null,
 *   filled?: number, total?: number, tileStates?: string[], tileVariant?: 'fill'|'status', tileLabels?: string[], to?: string|object, ariaLabel?: string, title?: string,
 *   visual?: import('react').ReactNode,
 *   rows?: Array<{ key: string, count: number|string, label: string, tone?: 'good'|'warn'|'bad'|'none', to?: string|object, linkLabel?: string }>,
 *   kpi?: string, testId?: string, children?: import('react').ReactNode, className?: string,
 * }} props
 * `children` replaces the value row (an action instead of a number). A missing value (null, undefined, '--')
 * shows a muted dash and no visual: never a zero.
 */
export default function KpiCard({
  label, value = null, unit = '', caption = null, tone, variant = 'none', percent = null,
  filled, total, tileStates: states, tileVariant = 'fill', tileLabels, rows, visual = null, to, ariaLabel, title, kpi, testId, children, className = '',
}) {
  const missing = value == null || value === '' || value === '--';
  const shown = missing ? '--' : value;
  const big = isNumberLike(shown);
  const ring = variant === 'ring' && !missing && finite(percent);
  const donut = !children && variant === 'donut' && !missing && finite(percent);
  // A percent sign belongs to its number ("34%", as formatScore writes it): a hairline gap, not a word gap.
  const valueRow = children ?? (
    <div className="mt-3 flex items-center justify-between gap-3">
      <div className={`flex min-w-0 flex-wrap items-baseline ${unit === '%' ? 'gap-x-0.5' : 'gap-x-2'} gap-y-1`}>
        <span
          className={`${big ? 'kpi-value' : 'kpi-value-text'} font-display font-extrabold leading-none tracking-tight tabular-nums ${missing ? 'text-ipc-muted' : 'text-ipc-primary'}`}
          data-kpi-value=""
        >
          {shown}
        </span>
        {unit && !missing ? <span className="text-base font-semibold text-ipc-muted sm:text-lg" data-kpi-unit="">{unit}</span> : null}
      </div>
      {ring ? <Ring percent={percent} /> : null}
    </div>
  );
  const body = (
    <>
      <div className="text-sm font-semibold text-ipc-muted">{label}</div>
      {valueRow}
      {!children && !missing && variant === 'bar' && finite(percent) ? <Bar percent={percent} /> : null}
      {!children && !missing && variant === 'tiles' ? <Tiles filled={filled} total={total} states={states} variant={tileVariant} labels={tileLabels} /> : null}
      {!children && !missing && variant === 'summary' ? <SummaryRows rows={rows} /> : null}
      {!children && !missing && variant === 'custom' && visual ? <div className="mt-4" data-kpi-visual="custom">{visual}</div> : null}
      {donut ? <Donut percent={percent} caption={caption} tone={tone} /> : null}
      {caption && !donut ? (
        <div className="mt-auto flex items-center gap-2 pt-4 text-sm text-ipc-muted" data-kpi-caption="">
          {tone && TONE_DOT[tone] ? <span className={`block h-2.5 w-2.5 shrink-0 rounded-sm ${TONE_DOT[tone]}`} aria-hidden="true" /> : null}
          <span className="min-w-0">{caption}</span>
        </div>
      ) : null}
    </>
  );
  const common = {
    'data-kpi': kpi ?? label,
    'data-testid': testId,
    title,
    style: { borderColor: 'var(--kpi-line)' },
  };
  if (to) {
    return (
      <Link to={to} aria-label={ariaLabel} className={`${FRAME} ${INTERACTIVE} ${className}`.trim()} {...common}>
        {body}
      </Link>
    );
  }
  return (
    <div className={`${FRAME} ${className}`.trim()} aria-label={ariaLabel} role={ariaLabel ? 'group' : undefined} {...common}>
      {body}
    </div>
  );
}

// The last row is never sparse (product owner, 2026-10-22): column counts divide the card count where they can, and
// where they cannot the cards of the last row stretch to fill it. In 2 columns an odd last card spans both; in the
// 3-column band of the 5-card row the grid has 6 tracks, every card spans 2, and a last row of 1 or 2 cards spans 6
// or 3 each. Every class is written out in full so Tailwind finds it in the source.
const GRID = Object.freeze({
  3: 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:max-lg:[&>:last-child:nth-child(odd)]:col-span-2',
  // 4 cards: 2 by 2 until 1280 px (no orphan card), then one row of 4 (the redesign mockups at 1440 px).
  4: 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4',
  5: [
    'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6 2xl:grid-cols-5',
    'sm:max-lg:[&>:last-child:nth-child(odd)]:col-span-2',
    'lg:max-2xl:[&>*]:col-span-2',
    'lg:max-2xl:[&>:last-child:nth-child(3n+1)]:col-span-6',
    'lg:max-2xl:[&>:last-child:nth-child(3n+2)]:col-span-3',
    'lg:max-2xl:[&>:nth-last-child(2):nth-child(3n+1)]:col-span-3',
  ].join(' '),
  6: 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6',
});

/** The responsive KPI row: 1 column on phones, 2 on tablets, 3 on desktop and up to `max` on wide screens (4: 2 by 2, then 4 from 1280 px). */
export function KpiGrid({ max = 4, className = '', children, ...rest }) {
  return <div className={`${GRID[max] ?? GRID[4]} ${className}`.trim()} {...rest}>{children}</div>;
}

/** The large page headline with its muted subline, where a page or section introduces KPIs. */
export function KpiIntro({ title, subline, as: Heading = 'h1', className = '' }) {
  return (
    <div className={className}>
      <Heading className="font-display text-3xl font-extrabold leading-tight tracking-tight text-ipc-primary sm:text-4xl lg:text-5xl">{title}</Heading>
      {subline ? <p className="mt-2 text-base text-ipc-muted sm:text-lg">{subline}</p> : null}
    </div>
  );
}
